/**
 * Page /admin/sauvegardes — statut des sauvegardes BDD (L13.9).
 *
 * Affichage uniquement (lecture seule). Le worker tourne en background sur
 * Render Cron Job. Cette page sert à :
 *   - Vérifier que le worker tourne (dernière sauvegarde réussie récente)
 *   - Surveiller la taille cumulée des backups stockés
 *   - Investiguer un échec (statut + message d'erreur)
 *
 * Pour TÉLÉCHARGER un dump, ça se fait depuis le dashboard Cloudflare R2
 * directement (l'app n'a pas accès au bucket vpc-backups par design).
 */

import { useEffect, useState } from 'react';
import { Card, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import {
  Database, CheckCircle2, XCircle, Loader2, AlertCircle, AlertTriangle, Clock,
  HardDrive, Calendar,
} from 'lucide-react';
import type { BackupStatusResponse, BackupLogEntry } from '@roronoa-collectivites/shared-types';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'jamais';
  const ageMs = Date.now() - new Date(iso).getTime();
  const ageMin = Math.floor(ageMs / 60_000);
  const ageHours = Math.floor(ageMs / 3_600_000);
  const ageDays = Math.floor(ageMs / 86_400_000);
  if (ageMin < 1) return 'à l\'instant';
  if (ageMin < 60) return `il y a ${ageMin} min`;
  if (ageHours < 24) return `il y a ${ageHours} h`;
  return `il y a ${ageDays} j`;
}

const TYPE_LABEL: Record<BackupLogEntry['type'], string> = {
  daily: 'Quotidien',
  monthly: 'Mensuel',
  manual: 'Manuel',
};

const TYPE_VARIANT: Record<BackupLogEntry['type'], 'neutral' | 'info' | 'success'> = {
  daily: 'neutral',
  monthly: 'info',
  manual: 'success',
};

export function BackupsStatusPage() {
  const [data, setData] = useState<BackupStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const status = await api.admin.backups.status(30);
        setData(status);
      } catch (err) {
        setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card variant="glass" padded>
        <div className="flex items-center gap-3 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span className="text-sm">Chargement du statut des sauvegardes…</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="error" icon={AlertCircle}>
        {error}
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex items-center gap-3">
        <LogoMark icon={Database} accent="tech" size="lg" />
        <div>
          <SectionEyebrow as="div">Administration</SectionEyebrow>
          <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
            Sauvegardes BDD
          </h1>
        </div>
      </div>

      {/* Le diagnostic du stockage vit ici : c'est la même nature de contrôle
          d'infrastructure que les sauvegardes, et c'est là qu'on vient quand on
          se demande « est-ce que ça tient ? ». */}
      <DiagnosticStockage />

      {/* Alerte critique / warning si pas de backup récent */}
      {data.alert && (
        <Alert
          variant={data.alert.severity === 'critical' ? 'error' : 'warning'}
          icon={data.alert.severity === 'critical' ? AlertCircle : AlertTriangle}
        >
          {data.alert.message}
        </Alert>
      )}

      {/* Résumé compteurs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card variant="glass" padded>
          <div className="flex items-center gap-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
            <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
            Réussies
          </div>
          <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-ink">
            {data.summary.successCount}
          </div>
          <div className="text-2xs text-ink-subtle">sur les {data.summary.total} dernières</div>
        </Card>

        <Card variant="glass" padded>
          <div className="flex items-center gap-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
            <XCircle className="h-3.5 w-3.5 text-urgent" aria-hidden="true" />
            Échouées
          </div>
          <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-ink">
            {data.summary.failedCount}
          </div>
          <div className="text-2xs text-ink-subtle">à investiguer</div>
        </Card>

        <Card variant="glass" padded>
          <div className="flex items-center gap-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
            <HardDrive className="h-3.5 w-3.5 text-tech" aria-hidden="true" />
            Stockage cumulé
          </div>
          <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-ink">
            {formatBytes(data.summary.totalSizeBytes)}
          </div>
          <div className="text-2xs text-ink-subtle">vpc-backups</div>
        </Card>

        <Card variant="glass" padded>
          <div className="flex items-center gap-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
            <Clock className="h-3.5 w-3.5 text-tech" aria-hidden="true" />
            Dernière OK
          </div>
          <div className="mt-1 font-display text-xl font-semibold text-ink">
            {formatRelativeTime(data.summary.lastSuccessAt)}
          </div>
          <div className="text-2xs text-ink-subtle">
            {data.summary.lastSuccessAt ? formatDateTime(data.summary.lastSuccessAt) : '—'}
          </div>
        </Card>
      </div>

      {/* Liste détaillée */}
      <Card variant="glass" padded={false}>
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-display text-base font-semibold text-ink">
            Historique récent
          </h2>
          <p className="mt-0.5 text-2xs text-ink-subtle">
            {data.items.length} entrée{data.items.length !== 1 ? 's' : ''} — pour télécharger un dump,
            utilise le dashboard Cloudflare R2 (bucket vpc-backups).
          </p>
        </div>

        {data.items.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Database className="mx-auto mb-2 h-8 w-8 text-ink-dim" aria-hidden="true" />
            <p className="text-sm text-ink-muted">
              Aucune sauvegarde enregistrée. Le worker n'a peut-être jamais tourné.
            </p>
            <p className="mt-2 text-2xs text-ink-subtle">
              Vérifie Render Cron Jobs ou lance manuellement :<br />
              <code className="font-mono">cd apps/server && pnpm tsx src/scripts/backup.ts</code>
            </p>
          </div>
        ) : (
          <>
            {/* Cards mobile */}
            <ul className="md:hidden divide-y divide-border" role="list">
              {data.items.map((b) => (
                <li key={b.id} className="p-3">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={b.status} />
                      <Badge variant={TYPE_VARIANT[b.type]} size="sm">{TYPE_LABEL[b.type]}</Badge>
                    </div>
                    <span className="text-2xs text-ink-subtle tabular-nums">
                      {formatRelativeTime(b.createdAt)}
                    </span>
                  </div>
                  <div className="text-xs text-ink">{formatDateTime(b.createdAt)}</div>
                  <div className="mt-1 flex items-center gap-3 text-2xs text-ink-muted tabular-nums">
                    <span>{formatBytes(b.sizeBytes)}</span>
                    {b.durationMs > 0 && <span>{formatDuration(b.durationMs)}</span>}
                    {b.environment && <span className="capitalize">{b.environment}</span>}
                  </div>
                  {b.errorMessage && (
                    <p className="mt-2 text-2xs text-urgent line-clamp-2">{b.errorMessage}</p>
                  )}
                </li>
              ))}
            </ul>

            {/* Table desktop */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface/40">
                  <tr className="text-left">
                    <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Quand</th>
                    <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Statut</th>
                    <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Type</th>
                    <th className="px-3 py-2 text-right text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Taille</th>
                    <th className="px-3 py-2 text-right text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Durée</th>
                    <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Env</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((b) => (
                    <tr key={b.id} className="border-b border-border last:border-b-0 hover:bg-surface/40">
                      <td className="px-3 py-2 text-ink">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3 text-ink-subtle" aria-hidden="true" />
                          {formatDateTime(b.createdAt)}
                        </div>
                        <div className="mt-0.5 text-2xs text-ink-subtle">{formatRelativeTime(b.createdAt)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={b.status} />
                        {b.errorMessage && (
                          <p className="mt-0.5 text-2xs text-urgent line-clamp-1 max-w-xs" title={b.errorMessage}>
                            {b.errorMessage}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={TYPE_VARIANT[b.type]} size="sm">{TYPE_LABEL[b.type]}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink">{formatBytes(b.sizeBytes)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink-muted">
                        {b.durationMs > 0 ? formatDuration(b.durationMs) : '—'}
                      </td>
                      <td className="px-3 py-2 text-ink-muted capitalize text-xs">{b.environment ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* Info ops */}
      <Card variant="glass" padded>
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg bg-tech-tint/70 text-tech-fg" aria-hidden="true">
            <Database className="h-4 w-4" />
          </div>
          <div className="text-sm">
            <h3 className="font-medium text-ink">Restauration en cas d'incident</h3>
            <ol className="mt-1 list-decimal pl-4 text-ink-muted space-y-1">
              <li>Télécharger le dump souhaité depuis le dashboard Cloudflare R2 (bucket <code className="font-mono text-2xs">vpc-backups</code>)</li>
              <li><code className="font-mono text-2xs">gunzip dump-YYYY-MM-DD-HH-MM-SS.sql.gz</code></li>
              <li><code className="font-mono text-2xs">psql DATABASE_URL &lt; dump-YYYY-MM-DD-HH-MM-SS.sql</code></li>
            </ol>
            <p className="mt-2 text-2xs text-ink-subtle">
              Rotation auto : 30 derniers daily + 12 derniers monthly.
              Le worker tourne via Render Cron Job à 03:00 UTC chaque jour.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: BackupLogEntry['status'] }) {
  if (status === 'success') {
    return (
      <Badge variant="success" size="sm">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        Réussi
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="urgent" size="sm">
        <XCircle className="h-3 w-3" aria-hidden="true" />
        Échec
      </Badge>
    );
  }
  return (
    <Badge variant="warning" size="sm">
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
      En cours
    </Badge>
  );
}


/**
 * DiagnosticStockage — l'aller-retour R2 réel, en un clic.
 *
 * ─── POURQUOI CE BOUTON EXISTE ──────────────────────────────────────────────
 *
 * La route `/api/admin/storage/diagnostic` a été livrée le 29/07 sans écran, et
 * c'était une erreur : l'access token de VPC vit **en mémoire**, jamais dans
 * `localStorage` (protection XSS, cf. `api.ts`). Il est donc IMPOSSIBLE
 * d'appeler la route depuis la console du navigateur ou une URL collée — et un
 * outil de diagnostic qu'on ne sait pas lancer ne diagnostique rien.
 *
 * ─── CE QU'IL VÉRIFIE ───────────────────────────────────────────────────────
 *
 * Les tests du dépôt de pièces simulent le stockage : l'appel S3 lui-même
 * (bucket, identifiants, région) ne peut se prouver que sur l'environnement
 * réel. Or TOUT dépôt en dépend — délibérations, urbanisme, scolaire,
 * signalements.
 *
 * Aucune donnée de la commune n'est touchée : sonde de quelques octets dans un
 * préfixe dédié, retirée en fin de course.
 */
function DiagnosticStockage() {
  const [rapport, setRapport] = useState<{
    configure: boolean; operationnel: boolean; premiere_rupture: string | null;
    etapes: Array<{
      etape: string; ce_qui_est_verifie: string; ok: boolean;
      detail: string | null; duree_ms: number | null;
    }>;
  } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function lancer() {
    setEnCours(true); setErreur(null); setRapport(null);
    try {
      setRapport(await api.storageDiagnostic());
    } catch (e) {
      setErreur(e instanceof ApiException ? e.payload.error : 'Diagnostic impossible');
    } finally { setEnCours(false); }
  }

  return (
    <Card variant="glass" padded>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-medium text-ink">Stockage des fichiers (R2)</h2>
          <p className="mt-0.5 text-sm text-ink-muted">
            Écrit un fichier de test sur le bucket, le relit, vérifie qu'il revient
            identique, puis le retire. C'est la seule vérification que les tests
            automatiques ne peuvent pas faire.
          </p>
        </div>
        <button type="button" onClick={() => void lancer()} disabled={enCours}
          className="inline-flex h-10 items-center gap-2 rounded-r-lg border border-border bg-surface/60 px-4 text-sm text-ink hover:bg-surface disabled:opacity-50">
          {enCours ? 'Vérification…' : 'Lancer le diagnostic'}
        </button>
      </div>

      {erreur && <Alert variant="error" className="mt-3">{erreur}</Alert>}

      {rapport && (
        <div className="mt-4">
          <Alert variant={rapport.operationnel ? 'success' : 'error'}>
            {rapport.operationnel
              ? 'Le stockage fonctionne. Les dépôts de pièces sont opérationnels.'
              : `Le stockage ne fonctionne pas. Première rupture : ${rapport.premiere_rupture}.`}
          </Alert>

          <ul className="mt-3 divide-y divide-border">
            {rapport.etapes.map((e) => (
              <li key={e.etape} className="py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={e.ok ? 'success' : 'urgent'} size="sm">
                    {e.ok ? 'OK' : 'ÉCHEC'}
                  </Badge>
                  <span className="text-sm text-ink">{e.etape}</span>
                  {e.duree_ms !== null && (
                    <span className="text-2xs text-ink-subtle">{e.duree_ms} ms</span>
                  )}
                </div>
                {/* Le rapport doit se lire sans être développeur : chaque étape
                    dit ce qu'elle prouve, pas seulement si elle passe. */}
                <p className="mt-0.5 text-2xs text-ink-subtle">{e.ce_qui_est_verifie}</p>
                {e.detail && (
                  <p className={`mt-0.5 text-2xs ${e.ok ? 'text-ink-muted' : 'text-urgent-fg'}`}>
                    {e.detail}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
